import classNames from 'classnames';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import SoftBadge from 'components/common/SoftBadge';
import { formatMoney } from 'helpers/utils';
import React, { useState, useMemo, useCallback } from 'react';
import { Col, Row, Button, Modal, Form } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import paths from 'routes/paths';
import { useDonorOrders, useUpdateOrderGiftAid } from 'hooks/useDonors';

const DonorGiving = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  // State for Gift Aid modal
  const [showGiftAidModal, setShowGiftAidModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newGiftAidStatus, setNewGiftAidStatus] = useState(false);

  // Fetch donor orders
  const { orders, isLoading: ordersLoading } = useDonorOrders(id);

  // Mutation for updating gift aid status
  const { updateGiftAidAsync } = useUpdateOrderGiftAid();

  // Handle opening the gift aid modal - wrapped in useCallback to prevent re-creation
  const handleOpenGiftAidModal = useCallback(order => {
    setSelectedOrder(order);
    setNewGiftAidStatus(order.gift_aid_eligible);
    setShowGiftAidModal(true);
  }, []);

  // Handle saving gift aid status
  const handleSaveGiftAidStatus = async () => {
    if (selectedOrder) {
      try {
        await updateGiftAidAsync({
          orderId: selectedOrder.id,
          giftAidEligible: newGiftAidStatus
        });
        setShowGiftAidModal(false);
      } catch (error) {
        // Error is handled by the hook
        console.error(error);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        Header: 'Ref',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const { id, simple_id } = rowData.row.original;
          return (
            <Link to={`${pathname}/${id}`} style={{ fontSize: '1rem' }}>
              {simple_id}
            </Link>
          );
        }
      },
      {
        accessor: 'item',
        Header: 'Item',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const { order_details, pledge } = rowData.row.original;
          return (
            <div>
              {pledge && (
                // Pledge payments are ordinary donations tagged with the pledge,
                // so they appear here AND on the pledge - label them so it's
                // clearly the same payment, not a duplicate.
                <Link to={paths.pledgeProfile.replace(':id', pledge.id)}>
                  <SoftBadge pill bg="info" className="mb-1">
                    Pledge payment · PL{pledge.simple_id}
                  </SoftBadge>
                </Link>
              )}
              {order_details.map((item, index) => {
                return (
                  <p
                    className={classNames('m-0', {
                      'mb-1': index !== order_details.length - 1
                    })}
                    key={index}
                    style={{ fontSize: '1rem' }}
                  >
                    {item.package
                      ? item.package.package_title
                      : 'Quick Donation'}
                  </p>
                );
              })}
            </div>
          );
        }
      },
      {
        accessor: 'billing',
        Header: 'Type',
        cellProps: { className: 'py-2' },
        Cell: rowData => {
          const details = rowData.row.original.order_details || [];
          const monthly = details.some(d => d.billing_condition === 'monthly');
          return (
            <SoftBadge pill bg={monthly ? 'info' : 'secondary'}>
              {monthly ? 'Monthly' : 'One-off'}
            </SoftBadge>
          );
        }
      },
      {
        accessor: 'date',
        Header: 'Date',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const { created_at } = rowData.row.original;
          const date = new Date(created_at).toLocaleDateString('en-GB');
          return <span style={{ fontSize: '1rem' }}>{date}</span>;
        }
      },
      {
        accessor: 'pay_method',
        Header: 'Pay Method',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const method = rowData.row.original.payment_method?.payment_name;
          if (!method) return '-';
          const bg = method === 'Cash' ? 'success' : 'primary';
          return (
            <SoftBadge bg={bg} pill>
              {method}
            </SoftBadge>
          );
        }
      },
      {
        accessor: 'status',
        Header: 'Status',
        cellProps: {
          className: 'fw-medium py-2'
        },
        Cell: rowData => {
          const { payment_status } = rowData.row.original;

          const getBadgeColor = status => {
            switch (status?.toLowerCase()) {
              case 'paid':
                return 'success';
              case 'pending':
                return 'warning';
              case 'failed':
                return 'danger';
              case 'refunded':
                return 'info';
              case 'cancelled':
                return 'secondary';
              default:
                return 'primary';
            }
          };

          return (
            <SoftBadge pill bg={getBadgeColor(payment_status)} className="me-2">
              {payment_status?.charAt(0).toUpperCase() +
                payment_status?.slice(1)}
            </SoftBadge>
          );
        }
      },
      {
        accessor: 'gift_aid_eligible',
        Header: 'Gift Aid Eligible',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const { gift_aid_eligible } = rowData.row.original;
          return (
            <div className="d-flex align-items-center">
              <SoftBadge
                pill
                bg={gift_aid_eligible ? 'success' : 'secondary'}
                className="me-2"
              >
                {gift_aid_eligible ? 'Yes' : 'No'}
              </SoftBadge>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-primary"
                onClick={() => handleOpenGiftAidModal(rowData.row.original)}
              >
                Change
              </Button>
            </div>
          );
        }
      },
      {
        accessor: 'donation_amount',
        Header: 'Amount',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => {
          const { donation_amount } = rowData.row.original;
          return (
            <span style={{ fontSize: '1rem' }}>
              {formatMoney(donation_amount)}
            </span>
          );
        }
      }
      // {
      //   accessor: 'idx',
      //   Header: '',
      //   cellProps: {
      //     className: 'py-2 text-end'
      //   },
      //   disableSortBy: true,

      //   Cell: rowData => {
      //     const { id } = rowData.row;
      //     return (
      //       <TableRowAction
      //         onCLickEdit={() => console.log(id)}
      //         onCLickDelete={() => console.log(id)}
      //         subject={permissionsKeyMap.donor.children.donors}
      //       />
      //     );
      //   }
      // }
    ],
    [pathname, handleOpenGiftAidModal]
  );

  return (
    <>
      {/* <Row className="g-3 mb-3">
        <Col md={4}>
          <Account user={donor} score={false} type={'Donor'} />
        </Col>
        <Col md={6}>
          <Card>
            <Carousel
              controls={false}
              interval={2000}
              className="donor-carousel"
              style={{
                height: 'inherit'
              }}
              onSlide={id => setActiveSlider(id)}
            >
              {data?.map(item => (
                <Carousel.Item key={item}>
                  <FalconCardHeader
                    title={
                      <p className="fs-0 mb-0">
                        Regular Donations{' '}
                        <FontAwesomeIcon
                          icon={'question-circle'}
                          className="text-400 ms-2"
                        />
                      </p>
                    }
                    light={false}
                    titleTag="div"
                    className="pb-1"
                    menuClassName="align-items-start"
                    endEl={
                      <IconButton
                        icon="pen"
                        variant="falcon-default"
                        className="fs--1 bg-transparent shadow-none p-0"
                      />
                    }
                  />

                  <Card.Body className="pt-0 pb-5">
                    <p className="fs-2 text-primary fw-medium mb-2">
                      {formatMoney(250.00)} <span className="fs--1 text-700">(RG10001)</span>
                    </p>
                    <p className="mb-2">
                      <FontAwesomeIcon
                        icon={'user'}
                        className="text-primary me-1"
                      />
                      Regular Giving - Monthly
                    </p>
                    <p className="mb-0">
                      <FontAwesomeIcon
                        icon={'credit-card'}
                        className="text-primary me-1"
                      />
                      Credit Card xx241
                    </p>
                  </Card.Body>
                </Carousel.Item>
              ))}
            </Carousel>
            <p className="mb-0 donor-carousel-counter fw-medium">
              {activeSlider + 1} of {data.length}
            </p>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Carousel
              controls={false}
              interval={2500}
              className="donor-carousel"
              onSlide={id => setActiveSlider2(id)}
            >
              {data?.map(item => (
                <Carousel.Item key={item}>
                  <FalconCardHeader
                    title={
                      <p className="fs-0 mb-0">
                        Pledges{' '}
                        <FontAwesomeIcon
                          icon={'question-circle'}
                          className="text-400 ms-2"
                        />
                      </p>
                    }
                    light={false}
                    titleTag="div"
                    className="pb-1"
                    menuClassName="align-items-start"
                    endEl={
                      <IconButton
                        icon="pen"
                        variant="falcon-default"
                        className="fs--1 bg-transparent shadow-none p-0"
                      />
                    }
                  />

                  <Card.Body className="pt-0 pb-5">
                    <p className="fs-2 text-primary fw-medium mb-2">
                      {formatMoney(950.00)}{' '}
                      <span className="fs--1 text-700">/ {formatMoney(1000.00)}</span>
                    </p>

                    <p className="mb-2">
                      <FontAwesomeIcon
                        icon={'credit-card'}
                        className="text-primary me-1"
                      />
                      PL100002 - Pledge
                    </p>
                    <p className="mb-0">
                      <FontAwesomeIcon
                        icon={'check-circle'}
                        className="text-success me-1"
                      />
                      Completed
                    </p>
                  </Card.Body>
                </Carousel.Item>
              ))}
            </Carousel>
            <p className="mb-0 donor-carousel-counter fw-medium">
              {activeSlider2 + 1} of {data.length}
            </p>
          </Card>
        </Col>
      </Row> */}

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={orders || []}
            columns={columns}
            title="Invoices"
            showSearchInput
            searchInputPlaceHolder="Search"
            addNew={false}
            loading={ordersLoading}
          />
        </Col>
      </Row>

      {/* Gift Aid Status Modal */}
      <Modal show={showGiftAidModal} onHide={() => setShowGiftAidModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.25rem' }}>
            Change Gift Aid Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <p className="mb-3" style={{ fontSize: '1rem' }}>
                <strong>Order ID:</strong> {selectedOrder.simple_id}
                <br />
                <strong>Amount:</strong>{' '}
                {formatMoney(selectedOrder.donation_amount)}
                <br />
                <strong>Current Status:</strong>{' '}
                <SoftBadge
                  pill
                  bg={selectedOrder.gift_aid_eligible ? 'success' : 'secondary'}
                >
                  {selectedOrder.gift_aid_eligible
                    ? 'Eligible'
                    : 'Not Eligible'}
                </SoftBadge>
              </p>

              <Form>
                <Form.Group>
                  <Form.Label style={{ fontSize: '1rem' }}>
                    Gift Aid Status
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    label="Eligible for Gift Aid"
                    style={{ fontSize: '1rem' }}
                    name="giftAidStatus"
                    checked={newGiftAidStatus === true}
                    onChange={() => setNewGiftAidStatus(true)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label="Not Eligible for Gift Aid"
                    style={{ fontSize: '1rem' }}
                    name="giftAidStatus"
                    checked={newGiftAidStatus === false}
                    onChange={() => setNewGiftAidStatus(false)}
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowGiftAidModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveGiftAidStatus}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DonorGiving;
